import { useCallback } from "react";
import { useCollapsibleViewContext } from "@/features/collapsible-view";
import { useDataModelContext } from "@/entities/data-model";
import { FOCUS_NAME_TO_ID } from "@/entities/form-metadata";
import { useSaveUserPreferencesMutation, useUserPreferencesQuery } from "@/entities/user-preferences";
import { preserveFormStateInCache } from "../form-history";
import type { FormValues } from "../form/types";
import { useFormControls } from "./useFormControls";

interface AdditionalData {
    matchAllTermsExactly: boolean;
    isSkipAssistPlacesModalPermanently?: boolean;
}

interface UseOnSubmitArgs {
    isEditForm: boolean;
}

export const useOnSubmit = ({ isEditForm }: UseOnSubmitArgs) => {
    const dataModel = useDataModelContext();
    const { handleSubmit } = useFormControls(dataModel, isEditForm);
    const { mutate: savePreferences } = useSaveUserPreferencesMutation();
    const { data: userPreference } = useUserPreferencesQuery();
    const { isCollapsed } = useCollapsibleViewContext();

    const onSubmit = useCallback(
        async (formValues: FormValues, additionalData: Partial<AdditionalData> = {}) => {
            preserveFormStateInCache(formValues);
            savePreferences({
                ...(formValues.name.givenName && {
                    givenNameExactness: formValues.name.givenNameExactness,
                }),
                ...(formValues.name.surname && { surnameExactness: formValues.name.surnameExactness }),
                types: formValues.types,
                showMoreOptions: !isCollapsed,
                showUnviewedRecordsOnly: formValues.showUnviewedRecordsOnly,
                collectionFocus: FOCUS_NAME_TO_ID[formValues.priority],
                ...additionalData,
            });
            await handleSubmit({ formValues, userPreference });
        },
        [handleSubmit, savePreferences, userPreference, isCollapsed],
    );

    return onSubmit;
};
